import { Button, IconButton, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { MAX_RECEIPT_ROWS } from './reimbursementLayout';
import { digitsFromCurrencyInput, formatCurrencyFromDigits } from './formatHelpers';

const formatGrandTotal = (amount) => amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// A currency-masked amount field, matching the Check Amount field above it:
// type digits and they fill in from the cents place ("$1,234.56").
const AmountField = ({ digits, onChange, fullWidth }) => (
    <TextField
        size="small"
        fullWidth={fullWidth}
        placeholder="$0.00"
        inputMode="numeric"
        value={formatCurrencyFromDigits(digits)}
        onChange={(e) => onChange(digitsFromCurrencyInput(e.target.value))}
    />
);

// Editable mirror of the template's "List All Receipts for Reimbursement"
// table - up to MAX_RECEIPT_ROWS (14, matching the paper form) rows of
// Purchased From / Amount / Purpose. Renders as a table on wider screens
// and as stacked cards on narrow ones, since a 3-column table doesn't fit
// comfortably on a phone.
const ReceiptsTable = ({ receipts, onRowChange, onAddRow, onRemoveRow, grandTotal }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const atLimit = receipts.length >= MAX_RECEIPT_ROWS;

    const headerCellStyle = {
        fontFamily: theme.typography.fontFamily,
        fontSize: '12px',
        fontWeight: 700,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        padding: '0 8px 8px',
        textAlign: 'left',
    };

    const cardStyle = {
        border: `1px solid ${theme.palette.brand.border}`,
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '10px',
    };

    const cardHeaderStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px',
    };

    const cardLabelStyle = {
        fontFamily: theme.typography.fontFamily,
        fontSize: '12px',
        fontWeight: 700,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    };

    return (
        <div>
            {isMobile ? (
                <div>
                    {receipts.map((receipt, index) => (
                        <div key={index} style={cardStyle}>
                            <div style={cardHeaderStyle}>
                                <span style={cardLabelStyle}>Receipt {index + 1}</span>
                                <IconButton
                                    size="small"
                                    onClick={() => onRemoveRow(index)}
                                    disabled={receipts.length === 1}
                                    aria-label={`Remove receipt ${index + 1}`}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Purchased From"
                                    placeholder="Store or vendor"
                                    value={receipt.purchasedFrom}
                                    onChange={(e) => onRowChange(index, 'purchasedFrom', e.target.value)}
                                />
                                <div>
                                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary, mb: '4px' }}>Amount</Typography>
                                    <AmountField
                                        fullWidth
                                        digits={receipt.amountDigits}
                                        onChange={(digits) => onRowChange(index, 'amountDigits', digits)}
                                    />
                                </div>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Purpose"
                                    placeholder="What was purchased"
                                    value={receipt.purpose}
                                    onChange={(e) => onRowChange(index, 'purpose', e.target.value)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
                        <thead>
                            <tr>
                                <th style={headerCellStyle}>Purchased From</th>
                                <th style={{ ...headerCellStyle, width: '160px' }}>Amount</th>
                                <th style={headerCellStyle}>Purpose</th>
                                <th style={{ ...headerCellStyle, width: '44px' }} />
                            </tr>
                        </thead>
                        <tbody>
                            {receipts.map((receipt, index) => (
                                <tr key={index}>
                                    <td style={{ padding: '4px' }}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Store or vendor"
                                            value={receipt.purchasedFrom}
                                            onChange={(e) => onRowChange(index, 'purchasedFrom', e.target.value)}
                                        />
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                        <AmountField
                                            fullWidth
                                            digits={receipt.amountDigits}
                                            onChange={(digits) => onRowChange(index, 'amountDigits', digits)}
                                        />
                                    </td>
                                    <td style={{ padding: '4px' }}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="What was purchased"
                                            value={receipt.purpose}
                                            onChange={(e) => onRowChange(index, 'purpose', e.target.value)}
                                        />
                                    </td>
                                    <td style={{ padding: '4px', textAlign: 'center' }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => onRemoveRow(index)}
                                            disabled={receipts.length === 1}
                                            aria-label={`Remove row ${index + 1}`}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                    <Button
                        startIcon={<AddIcon />}
                        onClick={onAddRow}
                        disabled={atLimit}
                        size="small"
                    >
                        Add Receipt
                    </Button>
                    {atLimit && (
                        <Typography
                            style={{
                                fontFamily: theme.typography.paragraphFontFamily,
                                fontSize: '12px',
                                color: theme.palette.text.secondary,
                                marginTop: '4px',
                            }}
                        >
                            Limited to {MAX_RECEIPT_ROWS} receipts per form.
                        </Typography>
                    )}
                </div>

                <Typography
                    style={{
                        fontFamily: theme.typography.fontFamily,
                        fontSize: '15px',
                        fontWeight: 700,
                        color: theme.palette.primary.dark,
                    }}
                >
                    Grand Total: {formatGrandTotal(grandTotal)}
                </Typography>
            </div>
        </div>
    );
};

export default ReceiptsTable;
